import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import Plot from 'react-plotly.js';
// This component assumes a WBT1.css file exists for styling.
// I am providing only the JS file as requested.
import './wbt1.css';
import { Loader } from './components/Components';
import HomePage from './pages/HomePage';
import GenePage from './pages/GenePage';
import PathwayPage from './pages/PathwayPage';
import CoExpressionPage from './pages/CoExpressionPage';
import AgingPage from './pages/AgingPage';
import ImmunePage from './pages/ImmunePage';
import MetabolicPage from './pages/MetabolicPage';
import PredictPage from './pages/PredictPage';
import ReadMePage from './pages/ReadMePage';
import DataPage from './pages/DataPage';
import AboutPage from './pages/AboutPage';

// --- Configuration ---
const API_URL = 'https://links.szapfs.org';



// --- Main App Component ---
function WBT1({ searchSelection }) {
    const pendingSearchRef = useRef(searchSelection);
    const handledSearchTs = useRef(null);
    const [activeTab, setActiveTab] = useState(null);
    const [loading, setLoading] = useState({ initial: true });
    const [options, setOptions] = useState({});
    const [selections, setSelections] = useState({});
    const [data, setData] = useState({});

const diseaseColors = useMemo(() => ({
  // Healthy
  "Healthy": "#666666",

  // Autoimmune / Rheumatologic
  "Rheumatoid Arthritis": "#AD1D78",
  "Ankylosing Spondylitis": "#5191B2",
  "ANCA-Associated Vasculitis": "#9E5A9A",
  "Atopic Dermatitis": "#C48FA8",
  "Familial Mediterranean Fever": "#8C5D99",
  "Systemic Lupus Erythematosus": "#CE5C86",
  "Scleroderma": "#B87AA3",

  // Cancer / Neoplastic
  "Lung Cancer": "#6AA692",
  "Stomach Cancer": "#D9967E",
  "Breast Cancer": "#FFDD00",
  "Colon and Rectum Cancer": "#7AAF9E",
  "Cervical Cancer": "#96C08E",
  "Liver Cancer": "#B30000",
  "Leukemia": "#D45A5A",
  "Bladder Cancer": "#E6A95F",
  "Esophageal Cancer": "#E0876C",
  "Lymphoma": "#82A8E0",
  "Musculoskeletal System Tumors": "#A48CB9",
  "Bile Duct Cancer": "#E4BE6F",
  "Pancreatic Cancer": "#8C4F4F",
  "Prostate Cancer": "#4F7FBF",

  // Cardiovascular
  "Congenital Heart Diseases": "#82B579",
  "Coronary Artery Disease": "#53C2EA",
  "Cerebral Venous Thrombosis": "#B37A6C",
  "Acute Rheumatic Fever": "#7D8DAF",
  "Deep Vein Thrombosis": "#5885A5",
  "Mitral Valve Prolapse": "#A1A8AA",
  "Arrhythmias": "#C8A1D1",
  "Hypertension": "#C97361",
  "Pulmonary Embolism": "#8DB7C4",
  "Premature Patent Ductus Arteriosus": "#6FAF7F",
  "Aortic and Mitral Valve Stenosis and Insufficiency": "#A98A8A",

  // Digestive / Hepatobiliary
  "Idiopathic Granulomatous Mastitis": "#66BD63",
  "Gallbladder Stones": "#95D4F5",
  "Gluten Enteropathy": "#D4BF8C",
  "Acute Pancreatitis": "#D87865",
  "Colon Volvulus": "#A2785F",

  // Renal / Urogenital
  "Renal Stones": "#77A6B6",
  "Benign Prostatic Hyperplasia": "#C5A86E",
  "Vesicoureteral Reflux": "#6CAFC4",
  "Gynecomastia": "#D69FBA",
  "Polycystic Kidney Disease": "#498C8C",
  "Pyelonephritis": "#BB6A6A",

  // Respiratory
  "Bronchopulmonary Dysplasia": "#B8C8E0",
  "Chronic Obstructive Pulmonary Disease": "#F9A266",
  "Spontaneous Pneumothorax": "#A7DACD",
  "Asthma": "#8FB3E8",

  // Endocrine / Metabolic
  "Polycystic Ovary Syndrome": "#199985",
  "Type 2 Diabetes": "#4A527A",
  "Type 1 Diabetes": "#404785",
  "Diabetic Complications": "#5A5F9C",
  "Childhood Hashimoto's Thyroiditis": "#F8BDD7",
  "Precocious Puberty": "#F0A3C2",
  "Idiopathic Direct Hyperbilirubinemia": "#E3C15C",
  "Childhood Hypothyroidism": "#C58BD4",

  // Infectious / Inflammatory
  "Sepsis": "#EA5699",
  "Nonalcoholic Steatohepatitis": "#AF9A63",
  "Brucellosis": "#A6B668",
  "Chronic Hepatitis B": "#BB864A",
  "Acute Poststreptococcal Glomerulonephritis": "#C27A7A",
  "Anaphylaxis": "#EFA958",
  "Hydatid Cyst": "#6DBE90",
  "Tuberculosis": "#C16B5A",

  // Musculoskeletal
  "Gonarthrosis": "#A0AEB4",
  "Rotator Cuff Tear": "#C6A88F",
  "Scoliosis": "#8C96BD",

  // Neurological / Psychiatric
  "Bipolar Disorder": "#DE6C7D",
  "Schizophrenia": "#FCCAB3",
  "Epilepsy": "#1280C4",
  "Parkinson's Disease": "#E64B35",
  "Multiple Sclerosis": "#9B5F87",
  "Dystonia": "#C08EA6",
  "Essential Tremor": "#B26996",
  "Alzheimer's Disease": "#A7A672",
  "Atypical Parkinsonism": "#C27C5A",
  "Blepharospasm": "#D1A3B7",
  "Major Depressive Disorder": "#8B88A1",
  "Febrile Convulsion": "#C49773",
  "Frontotemporal Dementia": "#A65D5D",
  "First-Episode Psychosis": "#D89797",
  "Neurofibromatosis": "#7A9CB2",

  // Pediatric / Congenital / Genetic
  "Nocturnal Enuresis": "#9ABCD3",
  "Childhood Renal Stones": "#7FB3C0",
  "Down Syndrome": "#D3A84F",
  "Non-Syndromic Congenital Anomaly": "#B4A6D1",
  "Congenital Deafness": "#8FB2A8",
  "Childhood Isolated Growth Hormone Deficiency": "#C9B9E0",

  // Dental / Oral
  "Apical Dental Abscess": "#B87070",
  "Periodontitis": "#D0A07A",

  // Ophthalmologic
  "Age-Related Macular Degeneration": "#9097A5",
  "Retinal Vein Occlusion": "#AD6D6D",
  "Glaucoma": "#4A7F8E",

  // Other / Systemic
  "Habitual Abortion": "#FBDAD9",
  "Longevity": "#D1CBE5",
  "Sudden Hearing Loss": "#91A6C4",
  "Carbon Monoxide Poisoning": "#9C3640",
  "Drug Intoxication": "#66287F"
}), []);

    const fetchData = useCallback(async (endpoint, dataKey, isSilent = false) => {
        if (!isSilent) setLoading(prev => ({ ...prev, [dataKey]: true }));
        try {
            const res = await fetch(`${API_URL}${endpoint}`);
            if (!res.ok) throw new Error(`Request failed: ${res.status}`);
            const jsonData = await res.json();
            setData(prev => ({ ...prev, [dataKey]: jsonData }));
            return jsonData;
        } catch (error) {
            console.error(`Error fetching ${dataKey}:`, error);
            if (!isSilent) setData(prev => ({ ...prev, [dataKey]: [] })); // Clear data on error
            return [];
        } finally {
            if (!isSilent) setLoading(prev => ({ ...prev, [dataKey]: false }));
        }
    }, []);

    const handleSelection = useCallback((key, value) => {
        setSelections(prev => ({ ...prev, [key]: value }));
    }, []);

    // Helper function to calculate linear regression
    const calculateTrendline = useCallback((data) => {
        if (!data || data.length < 2) return null;

        // Convert strings to numbers and filter out any invalid data points
        const validData = data.map(d => ({
            AGE: parseFloat(d.AGE),
            expression: parseFloat(d.expression)
        })).filter(d =>
            d &&
            !isNaN(d.AGE) &&
            !isNaN(d.expression) &&
            isFinite(d.AGE) &&
            isFinite(d.expression)
        );

        if (validData.length < 2) return null;

        const n = validData.length;
        const sumX = validData.reduce((sum, d) => sum + d.AGE, 0);
        const sumY = validData.reduce((sum, d) => sum + d.expression, 0);
        const sumXY = validData.reduce((sum, d) => sum + (d.AGE * d.expression), 0);
        const sumXX = validData.reduce((sum, d) => sum + (d.AGE * d.AGE), 0);

        const denominator = n * sumXX - sumX * sumX;
        if (denominator === 0) return null; // Avoid division by zero

        const slope = (n * sumXY - sumX * sumY) / denominator;
        const intercept = (sumY - slope * sumX) / n;

        const minX = Math.min(...validData.map(d => d.AGE));
        const maxX = Math.max(...validData.map(d => d.AGE));

        return {
            x: [minX, maxX],
            y: [slope * minX + intercept, slope * maxX + intercept]
        };
    }, []);

    // Initial load for all dropdowns
    useEffect(() => {
        const fetchAllOptions = async () => {
            const endpoints = {
                diseases: '/options/diseases', genes: '/options/genes', pathways: '/options/pathways',
                modules: '/options/modules', subsystems: '/options/subsystems', agingGenes: '/aging/correlated/genes',
                reportedAgingGenes: '/aging/reported/genes', agingModules: '/aging/modules', 
                immuneCellTypesHPA: '/aging/celltypes/HPA', immuneCellTypesMonaco: '/aging/celltypes/Monaco',
                predictDiseases: '/predict/diseases'
            };
            try {
                const results = await Promise.all(Object.entries(endpoints).map(([key, endpoint]) =>
                    fetch(`${API_URL}${endpoint}`).then(res => res.json()).then(data => ({ key, data }))
                ));
                const newOptions = results.reduce((acc, { key, data }) => ({ ...acc, [key]: data }), {});
                setOptions(newOptions);
                const defaults = {
                    geneAnalysisMode: 'gene', gene: newOptions.genes?.[0] || '', disease: newOptions.diseases?.[0] || '',
                    pathwayAnalysisMode: 'pathway', pathway: newOptions.pathways?.[0] || '', pathwayDisease: newOptions.diseases?.[0] || '',
                    module: newOptions.modules?.[0] || '',
                    agingAnalysisMode: 'genes', agingReportedGene: newOptions.reportedAgingGenes?.[0] || '', agingCorrelatedGene: newOptions.agingGenes?.[0] || '',
                    agingModule: newOptions.agingModules?.[0] || '', agingCellRef: 'HPA', agingCellType: newOptions.immuneCellTypesHPA?.[0] || '', agingSubsystem: newOptions.subsystems?.[0] || '', agingReaction: '', agingCorDir: 'Positive',
                    immuneCellRef: 'HPA', immuneCellType: newOptions.immuneCellTypesHPA?.[0] || '', immuneDiversityCell: 'TCR', immuneDiversityMetric: 'chao1',
                    metabolicSubsystem: newOptions.subsystems?.[0] || '', metabolicReaction: '',
                    predictDisease: newOptions.predictDiseases?.[0] || newOptions.diseases?.filter(d => d !== 'healthy')[0] || '', shapGene: '',
                };

                // If there's a pending search selection, apply it on top of defaults
                const pending = pendingSearchRef.current;
                if (pending) {
                    const sectionMap = {
                        'gene': { geneAnalysisMode: 'gene', gene: pending.value },
                        'disease_deg': { geneAnalysisMode: 'disease', disease: pending.value },
                        'pathway': { pathwayAnalysisMode: 'pathway', pathway: pending.value },
                        'disease_pathway': { pathwayAnalysisMode: 'disease', pathwayDisease: pending.value },
                        'co-expression': { module: pending.value },
                        'aging_reported': { agingAnalysisMode: 'genes', agingReportedGene: pending.value },
                        'aging_correlated': { agingAnalysisMode: 'genes', agingCorrelatedGene: pending.value },
                        'aging_cluster': { agingAnalysisMode: 'clusters', agingModule: pending.value },
                        'celltype': { agingAnalysisMode: 'cells', agingCellType: pending.value },
                        'subsystem': { metabolicSubsystem: pending.value },
                        'reaction': { metabolicReaction: pending.value },
                        'prediction': { predictDisease: pending.value },
                        'shap_gene': { shapGene: pending.value },
                    };
                    const overrides = sectionMap[pending.section];
                    if (overrides) {
                        Object.assign(defaults, overrides);
                    }
                    setActiveTab(pending.tab);
                    handledSearchTs.current = pending._ts;
                } else {
                    setActiveTab('gene');
                }

                setSelections(defaults);
            } catch (error) {
                console.error('[WBT1] fetchAllOptions FAILED:', error);
                alert("FATAL ERROR: Could not fetch initial options from the backend. Please check the server and network connection.");
            } finally {
                setLoading({ initial: false });
            }
        };
        fetchAllOptions();
    }, []);

    // --- Dependent Dropdowns Logic ---
    useEffect(() => {
        if (!selections.agingSubsystem) return;
        console.log('Fetching reactions for subsystem:', selections.agingSubsystem);
        fetchData(`/aging/subsystem/${selections.agingSubsystem}/reactions`, 'agingReactions', true).then(reactions => {
            if (reactions?.length) {
                setOptions(prev => ({ ...prev, agingReactions: reactions }));
                handleSelection('agingReaction', reactions[0]);
            }
        });
    }, [selections.agingSubsystem, fetchData, handleSelection]);

    useEffect(() => {
        if (!selections.metabolicSubsystem) return;
        fetchData(`/aging/subsystem/${selections.metabolicSubsystem}/reactions`, 'metabolicReactions', true).then(reactions => {
            if (reactions?.length) handleSelection('metabolicReaction', reactions[0]);
        });
    }, [selections.metabolicSubsystem, fetchData, handleSelection]);

    // Update cell types when reference changes
    useEffect(() => {
        if (selections.agingCellRef) {
            const cellTypes = selections.agingCellRef === 'HPA' ? options.immuneCellTypesHPA : options.immuneCellTypesMonaco;
            if (cellTypes?.length && !cellTypes.includes(selections.agingCellType)) {
                handleSelection('agingCellType', cellTypes[0]);
            }
        }
    }, [selections.agingCellRef, options.immuneCellTypesHPA, options.immuneCellTypesMonaco, selections.agingCellType, handleSelection]);

    useEffect(() => {
        if (selections.immuneCellRef) {
            const cellTypes = selections.immuneCellRef === 'HPA' ? options.immuneCellTypesHPA : options.immuneCellTypesMonaco;
            if (cellTypes?.length && !cellTypes.includes(selections.immuneCellType)) {
                handleSelection('immuneCellType', cellTypes[0]);
            }
        }
    }, [selections.immuneCellRef, options.immuneCellTypesHPA, options.immuneCellTypesMonaco, selections.immuneCellType, handleSelection]);

    // --- Handle search selection from global search bar ---
    // Only runs when initial load is already done (i.e. searching while inside the atlas).
    // Navigation from outside is handled by the initial load effect above via pendingSearchRef.
    useEffect(() => {
        if (!searchSelection) return;
        if (loading.initial) return;
        if (searchSelection._ts === handledSearchTs.current) return;
        const { tab, section, value } = searchSelection;

        setActiveTab(tab);

        const sectionMap = {
            'gene': { geneAnalysisMode: 'gene', gene: value },
            'disease_deg': { geneAnalysisMode: 'disease', disease: value },
            'pathway': { pathwayAnalysisMode: 'pathway', pathway: value },
            'disease_pathway': { pathwayAnalysisMode: 'disease', pathwayDisease: value },
            'co-expression': { module: value },
            'aging_reported': { agingAnalysisMode: 'genes', agingReportedGene: value },
            'aging_correlated': { agingAnalysisMode: 'genes', agingCorrelatedGene: value },
            'aging_cluster': { agingAnalysisMode: 'clusters', agingModule: value },
            'celltype': { agingAnalysisMode: 'cells', agingCellType: value },
            'subsystem': { metabolicSubsystem: value },
            'reaction': { metabolicReaction: value },
            'prediction': { predictDisease: value },
            'shap_gene': { shapGene: value },
        };

        const updates = sectionMap[section];
        if (updates) {
            setSelections(prev => ({ ...prev, ...updates }));
        }
    }, [searchSelection, loading.initial]);

    // --- Data Fetching by Tab ---
    const useDebouncedEffect = (effect, deps, delay) => {
        useEffect(() => {
            const handler = setTimeout(() => effect(), delay);
            return () => clearTimeout(handler);
        // eslint-disable-next-line react-hooks/exhaustive-deps
        }, deps);
    };

    useDebouncedEffect(() => {
        if (activeTab === 'gene') {
            if (selections.geneAnalysisMode === 'gene' && selections.gene) {
                fetchData(`/gene/${selections.gene}/tpm`, 'geneTpm');
                fetchData(`/gene/${selections.gene}/deg`, 'geneDeg');
                fetchData(`/gene/${selections.gene}/markers`, 'geneMarkers');
                fetchData(`/gene/${selections.gene}/expression_boxplot`, 'geneExpressionBoxplot');

            } else if (selections.geneAnalysisMode === 'disease' && selections.disease) {
                fetchData(`/disease/${selections.disease}/deg/volcano`, 'diseaseVolcano');
                fetchData(`/disease/${selections.disease}/deg/up`, 'diseaseUp');
                fetchData(`/disease/${selections.disease}/deg/down`, 'diseaseDown');
            }
        } else if (activeTab === 'pathway') {
            if (selections.pathwayAnalysisMode === 'pathway' && selections.pathway) {
                fetchData(`/pathway/${selections.pathway}/diseases`, 'pathwayDiseases');
            } else if (selections.pathwayAnalysisMode === 'disease' && selections.pathwayDisease) {
                fetchData(`/disease/${selections.pathwayDisease}/pathways`, 'diseasePathways');
            }
        } else if (activeTab === 'co-expression' && selections.module) {
            fetchData(`/module/${selections.module}/genes`, 'moduleGenes');
            fetchData(`/module/${selections.module}/gsoa`, 'moduleGsoa');
            fetchData(`/module/${selections.module}/eigengenes`, 'moduleEigengenes');
        } else if (activeTab === 'aging') {
            const mode = selections.agingAnalysisMode;
            if (mode === 'genes') {
                if(selections.agingReportedGene) fetchData(`/aging/reported/${selections.agingReportedGene}`, 'agingReportedData');
                if(selections.agingCorrelatedGene) fetchData(`/aging/correlated/${selections.agingCorrelatedGene}`, 'agingCorrelatedData');
            } else if (mode === 'clusters' && selections.agingModule) {
                 fetchData(`/aging/module/${selections.agingModule}/correlation`, 'agingModuleCorr');
                 fetchData(`/module/${selections.agingModule}/genes`, 'agingModuleGenes');
                 fetchData(`/module/${selections.agingModule}/gsoa`, 'agingModuleGsoa');
                 fetchData(`/module/${selections.agingModule}/eigengenes`, 'agingModuleEigengenes');
            } else if (mode === 'cells' && selections.agingCellType) {
                 fetchData(`/aging/cells/${selections.agingCellRef}/${selections.agingCellType}`, 'agingCellData');
                 fetchData(`/aging/cells/correlation/${selections.agingCellRef}/${selections.agingCellType}`, 'agingCellCorr');
            } else if (mode === 'metabolism') {
                if (selections.agingReaction) {
                    fetchData(`/aging/reaction/${selections.agingReaction}`, 'agingReactionData');
                    fetchData(`/aging/reaction/correlation/${selections.agingReaction}`, 'agingReactionCorr');
                }
                if (selections.agingCorDir) fetchData(`/aging/metabolic/${selections.agingCorDir}`, 'agingMetabolicFiltered');
            }
        } else if (activeTab === 'immune') {
            if (selections.immuneCellRef && selections.immuneCellType) fetchData(`/immune/prop/${selections.immuneCellRef}/${selections.immuneCellType}`, 'immunePropData');
            if (selections.immuneDiversityCell && selections.immuneDiversityMetric) fetchData(`/immune/diversity/${selections.immuneDiversityCell}/${selections.immuneDiversityMetric}`, 'immuneDiversityData');
        } else if (activeTab === 'metabolic') {
            if (selections.metabolicSubsystem) {
                fetchData(`/metabolic/subsystem/${selections.metabolicSubsystem}`, 'metabolicSubsystemData');
                // Also fetch reactions for this subsystem and update options
                fetchData(`/aging/subsystem/${selections.metabolicSubsystem}/reactions`, 'tempReactions', true).then(reactions => {
                    if (reactions?.length) {
                        setOptions(prev => ({ ...prev, metabolicReactions: reactions }));
                        if (!selections.metabolicReaction || !reactions.includes(selections.metabolicReaction)) {
                            handleSelection('metabolicReaction', reactions[0]);
                        }
                    }
                });
            }
            if (selections.metabolicReaction) fetchData(`/metabolic/reaction/${selections.metabolicReaction}`, 'metabolicReactionData');
        } else if (activeTab === 'prediction') {
            if (selections.predictDisease) fetchData(`/predict/${selections.predictDisease}/shap`, 'predictShap');
            if (selections.shapGene) fetchData(`/predict/deg/${selections.shapGene}`, 'predictShapDeg');
        }
    }, [selections, activeTab, fetchData], 100);

    // --- RENDER LOGIC ---
    const renderContent = () => {
        if (loading.initial) return <Loader text="Fetching initial data..." />;
        
        const isTabLoading = Object.values(loading).some(Boolean) && !loading.initial;

        switch (activeTab) {

            case 'gene':
                return <GenePage
                    selections={selections}
                    handleSelection={handleSelection}
                    options={options}
                    data={data}
                    isTabLoading={isTabLoading}
                    diseaseColors={diseaseColors}
                />;
            case 'pathway':
                return <PathwayPage
                    selections={selections}
                    handleSelection={handleSelection}
                    options={options}
                    data={data}
                    isTabLoading={isTabLoading}
                />;
            case 'co-expression':
                return <CoExpressionPage
                    selections={selections}
                    handleSelection={handleSelection}
                    options={options}
                    data={data}
                    isTabLoading={isTabLoading}
                    diseaseColors={diseaseColors}
                />;
            case 'aging':
                return <AgingPage
                    selections={selections}
                    handleSelection={handleSelection}
                    options={options}
                    data={data}
                    isTabLoading={isTabLoading}
                    calculateTrendline={calculateTrendline}
                />;
            case 'immune':
                return <ImmunePage
                    selections={selections}
                    handleSelection={handleSelection}
                    options={options}
                    data={data}
                    isTabLoading={isTabLoading}
                    diseaseColors={diseaseColors}
                />;
            case 'metabolic':
                return <MetabolicPage
                    selections={selections}
                    handleSelection={handleSelection}
                    options={options}
                    data={data}
                    isTabLoading={isTabLoading}
                    diseaseColors={diseaseColors}
                />;
                case 'prediction':
                    return <PredictPage
                        selections={selections}
                        handleSelection={handleSelection}
                        options={options}
                        data={data}
                        isTabLoading={isTabLoading}
                        loading={loading}
                        diseaseColors={diseaseColors}  // Add this line
                    />;
            case 'Read Me':
                return <ReadMePage />;
            case 'data':
                return <DataPage />;
            case 'about':
                return <AboutPage />;
            default: return <div className="PHAWBT-static-page"><h2>Under Construction</h2><p>This tab ('{activeTab}') is under development.</p></div>
        }
    };

    return (
        <div className="PHAWBT-container">
            <nav className="PHAWBT-sidebar">
                    {['gene', 'pathway', 'co-expression', 'aging', 'immune', 'metabolic', 'prediction', 'Read Me', 'data'].map(tab => (
                        <li key={tab} onClick={() => setActiveTab(tab)} className={activeTab === tab ? 'PHAWBT-active' : ''}>
                            {tab.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </li>
                    ))}
            </nav>
            <div className="PHAWBT-content">
                {loading.initial ? <div> </div> : renderContent()}
            </div>
        </div>
    );
}

export default WBT1;